import { Add } from "@faire/slate-icons/Add";
import { Heart } from "@faire/slate-icons/Heart";
import { HeartSolid } from "@faire/slate-icons/HeartSolid";
import { Search } from "@faire/slate-icons/Search";
import { Checkbox } from "@faire/slate/components/checkbox";
import { IconButton } from "@faire/slate/components/icon-button";

import { cn } from "@faire/prototype-playground/lib/utils";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

export interface CollectionProductTileData {
  token: string;
  name: string;
  imageUrl: string;
  /** Wholesale price, formatted (e.g. "$11"). */
  price: string;
  /** MSRP line, formatted (e.g. "MSRP $23.98"). */
  msrp?: string;
  /** Case size line (e.g. "Case of 6"). */
  caseSize?: string;
}

export interface CollectionProductTileProps {
  product: CollectionProductTileData;
  isFavorited?: boolean;
  onFavorite?: () => void;
  onQuickAdd?: () => void;
  onFindSimilar?: () => void;
  /**
   * Bulk multi-select mode. Hides favorite / find-similar / quick-add, shows a
   * grey (`border-fs-border-muted`) outline on every tile, turns black
   * (`border-fs-action-border-active`) on the selected tile(s), and makes the
   * whole tile a single toggle target.
   */
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  className?: string;
}

/**
 * Collection-page product tile: square image with favorite heart, quick-add,
 * and find-similar in the default shelf state — or a bulk multi-select
 * overlay (grey → black floating outline, decorative top-left checkbox,
 * whole-tile toggle) when `selectMode` is on. The favorite heart stays
 * visible and independently clickable in both states; quick-add and
 * find-similar hide in `selectMode`.
 */
export default function CollectionProductTile({
  product,
  isFavorited = false,
  onFavorite,
  onQuickAdd,
  onFindSimilar,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
  className,
}: CollectionProductTileProps) {
  const { src, onError } = usePlaygroundImageWithFallback(product.imageUrl);
  const favoriteLabel = usePlaygroundLocalMsg(
    isFavorited ? "Saved to board" : "Save product to board"
  );
  const quickAddLabel = usePlaygroundLocalMsg("Quick add");
  const findSimilarLabel = usePlaygroundLocalMsg("Find similar products");
  const selectLabel = usePlaygroundLocalMsg("{name}, select product", {
    name: product.name,
  });

  // `outline` (rather than `border`) so the selection ring floats 1px clear
  // of the image edge without eating into the tile's layout box.
  const imageOutlineCls = selectMode
    ? isSelected
      ? "outline-fs-action-border-active"
      : "outline-fs-border-muted"
    : "outline-transparent";

  return (
    <div className={cn("relative flex flex-col", className)}>
      <div className="relative">
        {/* Image */}
        <div
          className={cn(
            "rounded-fs-component-default relative w-full overflow-hidden aspect-square outline-2 outline-offset-2 transition-colors duration-150",
            imageOutlineCls
          )}
        >
          <img
            src={src}
            alt={product.name}
            onError={onError}
            referrerPolicy="no-referrer"
            className="rounded-fs-component-default absolute top-0 left-0 h-full w-full object-cover object-center"
          />
        </div>

        {selectMode ? (
          <>
            <button
              type="button"
              onClick={onToggleSelect}
              aria-pressed={isSelected}
              aria-label={selectLabel}
              data-test-id={`select-tile-${product.token}`}
              className="rounded-fs-component-default focus-visible:outline-fs-action-border-focus_ring absolute inset-0 z-4 block cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1"
            />
            <div
              className="bg-fs-surface-primary rounded-fs-container-default pointer-events-none absolute top-2 left-2 z-5 inline-flex"
              data-test-id={`select-checkbox-${product.token}`}
            >
              <Checkbox checked={isSelected} tabIndex={-1} readOnly aria-hidden />
            </div>
          </>
        ) : null}

        {/* Favorite (top-right) — stays visible in select mode too. Bare
            icon (no IconButton chrome), sized and positioned to match the
            top-left checkbox's box exactly (`--fs-checkbox-box-size`, 16px). */}
        <button
          type="button"
          aria-label={favoriteLabel}
          aria-pressed={isFavorited}
          onClick={(event) => {
            event.stopPropagation();
            onFavorite?.();
          }}
          className="absolute top-2 right-2 z-5 flex size-4 items-center justify-center"
        >
          {isFavorited ? (
            <HeartSolid className="size-4" titleAccess="" />
          ) : (
            <Heart className="size-4" titleAccess="" />
          )}
        </button>

        {!selectMode ? (
          <>
            {/* Find similar (bottom-left) */}
            <div className="absolute bottom-2 left-2 z-4">
              <IconButton
                Icon={Search}
                aria-label={findSimilarLabel}
                onClick={onFindSimilar}
                variant="secondary"
                size="xSmall"
              />
            </div>

            {/* Quick add (bottom-right) */}
            <div className="absolute right-2 bottom-2 z-4">
              <IconButton
                Icon={Add}
                aria-label={quickAddLabel}
                onClick={onQuickAdd}
                variant="secondary"
                size="xSmall"
              />
            </div>
          </>
        ) : null}
      </div>

      {/* Price + name */}
      <div className="mt-2 flex flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-x-1">
          <span className="type-fs-subheading-medium text-fs-text-primary">
            {product.price}
          </span>
          {product.msrp ? (
            <span className="type-fs-label text-fs-text-subdued">
              {product.msrp}
            </span>
          ) : null}
        </div>
        <p className="type-fs-paragraph-medium text-fs-text-primary line-clamp-2 break-words">
          {product.name}
        </p>
        {product.caseSize ? (
          <p className="type-fs-paragraph text-fs-text-subdued">
            {product.caseSize}
          </p>
        ) : null}
      </div>
    </div>
  );
}
